'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  SearchOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  FilterOutlined,
  CheckOutlined,
  CalendarOutlined,
} from '@ant-design/icons';

const QUICK_FEATURES = [
  'Pet Friendly',
  'Powered Sites',
  'Toilets',
  'Hot Showers',
  'Drinking Water',
  'Campfires Allowed',
  'WiFi',
  'Dump Point',
  'Swimming Pool',
  'Beach Access',
  'River Access',
  'Fishing',
  'Hiking Trails',
  '4WD Access',
  'BBQ',
  'Camp Kitchen',
  'Playground',
  'Family Friendly',
  'Cabins',
  'Laundry',
];

interface MapToolbarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  onLocationSearch: (value: string) => void;
  locationSuggestions: { value: string }[];
  // Kept for API compatibility with page.tsx; type filtering now lives in the
  // on-map category chips (instant client-side filter) rather than a second
  // redundant pill row in this toolbar.
  selectedType: string;
  onTypeChange: (type: string) => void;
  selectedFeatures: string[];
  onToggleFeature: (feature: string) => void;
  onClearFilters: () => void;
  // Redundant on the fullscreen map (a List button + a "Update map as it
  // moves" checkbox already float over the map), so no longer rendered here.
  showList: boolean;
  onToggleList: () => void;
  searchAsMove: boolean;
  onToggleSearchAsMove: () => void;
  entityCount: number;
  isSearching: boolean;
  checkInDate?: string;
  checkOutDate?: string;
  onDateChange?: (checkIn: string, checkOut: string) => void;
  availabilityActive?: boolean;
}

const MapToolbar: React.FC<MapToolbarProps> = ({
  searchQuery,
  onSearchChange,
  onSearch,
  onLocationSearch,
  locationSuggestions,
  selectedFeatures,
  onToggleFeature,
  onClearFilters,
  isSearching,
  checkInDate,
  checkOutDate,
  onDateChange,
  availabilityActive,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const datesPanelRef = useRef<HTMLDivElement>(null);

  const activeFilterCount = selectedFeatures.length;

  // Close suggestions / panels on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target as Node)) {
        const btn = document.getElementById('filter-toggle-btn');
        if (btn && !btn.contains(e.target as Node)) setFiltersOpen(false);
      }
      if (datesPanelRef.current && !datesPanelRef.current.contains(e.target as Node)) {
        const btn = document.getElementById('dates-toggle-btn');
        if (btn && !btn.contains(e.target as Node)) setDatesOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setShowSuggestions(false);
      onSearch();
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false);
      searchInputRef.current?.blur();
    }
  }, [onSearch]);

  const handleSearchInput = useCallback((value: string) => {
    onSearchChange(value);
    if (value.length >= 2) {
      onLocationSearch(value);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  }, [onSearchChange, onLocationSearch]);

  const handleSuggestionClick = useCallback((suggestion: string) => {
    onSearchChange(suggestion);
    setShowSuggestions(false);
    setTimeout(() => onSearch(), 50);
  }, [onSearchChange, onSearch]);

  return (
    <div className="absolute top-0 left-0 right-0 z-20">
      {/* One clean row: a prominent, always-visible search field is the hero;
          Filters and Dates sit alongside as round controls. Type filtering
          lives in the on-map category chips just below. */}
      <div className="flex items-center gap-2 px-2.5 py-2.5 bg-white/95 backdrop-blur-md border-b border-gray-200/80 shadow-sm lg:px-3">
        {/* Search — always open, full width */}
        <div ref={searchContainerRef} className="relative flex-1 min-w-0">
          <SearchOutlined
            className={`pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm transition-colors ${
              isSearching ? 'text-accent animate-pulse' : 'text-accent'
            }`}
          />
          <input
            ref={searchInputRef}
            type="text"
            inputMode="search"
            enterKeyHint="search"
            value={searchQuery}
            onChange={(e) => handleSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
            placeholder="Search a town, park or region"
            aria-label="Search campsites by location"
            className="w-full h-11 pl-10 pr-9 text-[15px] rounded-full border border-gray-300 bg-white shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => { onSearchChange(''); setShowSuggestions(false); searchInputRef.current?.focus(); }}
              aria-label="Clear search"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            >
              <CloseOutlined className="text-[11px]" />
            </button>
          )}

          {/* Location suggestions */}
          {showSuggestions && locationSuggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-200 py-1.5 max-h-[280px] overflow-y-auto">
              {locationSuggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => handleSuggestionClick(s.value)}
                  className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-[#e3f2fd] hover:text-[#1558b0] flex items-center gap-2.5"
                >
                  <EnvironmentOutlined className="text-xs text-gray-400 flex-shrink-0" />
                  <span className="truncate">{s.value}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Filters */}
        <button
          id="filter-toggle-btn"
          onClick={() => { setFiltersOpen((v) => !v); setDatesOpen(false); }}
          aria-label="Filter by features"
          aria-pressed={filtersOpen || activeFilterCount > 0}
          className={`relative flex-shrink-0 h-11 w-11 flex items-center justify-center rounded-full border transition-colors ${
            filtersOpen || activeFilterCount > 0
              ? 'bg-[#1558b0] text-white border-[#1558b0] shadow-sm'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
          }`}
        >
          <FilterOutlined className="text-base" />
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold ring-2 ring-white">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Dates */}
        {onDateChange && (
          <button
            id="dates-toggle-btn"
            onClick={() => { setDatesOpen((v) => !v); setFiltersOpen(false); }}
            aria-label="Check availability by date"
            aria-pressed={datesOpen || !!availabilityActive}
            className={`flex-shrink-0 h-11 w-11 flex items-center justify-center rounded-full border transition-colors ${
              availabilityActive || datesOpen
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <CalendarOutlined className="text-base" />
          </button>
        )}
      </div>

      {/* Filter dropdown panel */}
      {filtersOpen && (
        <div
          ref={filterPanelRef}
          className="mx-2.5 lg:mx-3 mt-2 bg-white rounded-2xl shadow-xl border border-gray-200 p-4 max-h-[300px] overflow-y-auto"
        >
          <div className="flex items-center justify-between mb-2.5">
            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">Features</h3>
            {selectedFeatures.length > 0 && (
              <button
                onClick={onClearFilters}
                className="text-[11px] text-[#1558b0] hover:text-[#123f7d] font-semibold"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {QUICK_FEATURES.map((feature) => {
              const isActive = selectedFeatures.includes(feature);
              return (
                <button
                  key={feature}
                  onClick={() => onToggleFeature(feature)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                    isActive
                      ? 'bg-[#1558b0] text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {isActive && <CheckOutlined className="text-[9px] mr-1" />}
                  {feature}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Date picker dropdown panel */}
      {datesOpen && onDateChange && (
        <div
          ref={datesPanelRef}
          className="mx-2.5 lg:mx-3 mt-2 bg-white rounded-2xl shadow-xl border border-gray-200 p-4"
        >
          <div className="flex items-center justify-between mb-2.5">
            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">Check availability</h3>
            {availabilityActive && (
              <button
                onClick={() => { onDateChange('', ''); setDatesOpen(false); }}
                className="text-[11px] text-[#1558b0] hover:text-[#123f7d] font-semibold"
              >
                Clear dates
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <label className="block text-[10px] text-gray-500 font-semibold mb-1 uppercase tracking-wide">Check in</label>
              <input
                type="date"
                value={checkInDate || ''}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) => {
                  const newCheckIn = e.target.value;
                  let newCheckOut = checkOutDate || '';
                  if (newCheckIn && (!newCheckOut || newCheckOut <= newCheckIn)) {
                    const d = new Date(newCheckIn);
                    d.setDate(d.getDate() + 2);
                    newCheckOut = d.toISOString().split('T')[0];
                  }
                  onDateChange(newCheckIn, newCheckOut);
                }}
                className="w-full h-10 px-2.5 text-sm border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            <div className="flex-1">
              <label className="block text-[10px] text-gray-500 font-semibold mb-1 uppercase tracking-wide">Check out</label>
              <input
                type="date"
                value={checkOutDate || ''}
                min={checkInDate || new Date().toISOString().split('T')[0]}
                onChange={(e) => onDateChange(checkInDate || '', e.target.value)}
                className="w-full h-10 px-2.5 text-sm border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
          </div>
          {availabilityActive && (
            <p className="mt-2.5 text-[11px] text-emerald-600 font-medium">
              Markers are colour-coded by availability for your dates
            </p>
          )}
        </div>
      )}

      {/* Active feature chips below toolbar */}
      {selectedFeatures.length > 0 && !filtersOpen && (
        <div className="flex items-center gap-1.5 px-2.5 lg:px-3 py-1.5 overflow-x-auto scrollbar-hide">
          {selectedFeatures.map((feature) => (
            <span
              key={feature}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium bg-[#1558b0] text-white rounded-full whitespace-nowrap shadow-sm"
            >
              {feature}
              <button onClick={() => onToggleFeature(feature)} aria-label={`Remove ${feature} filter`} className="hover:text-[#e3f2fd]">
                <CloseOutlined className="text-[8px]" />
              </button>
            </span>
          ))}
          <button
            onClick={onClearFilters}
            className="text-[11px] text-gray-500 hover:text-gray-700 font-semibold whitespace-nowrap ml-1"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
};

export default MapToolbar;
